// Phase H/I — Threat Intel — v2 MUI migration (LDCP-160).
//
// Three tabs: Managed rules / Activity / Contribution.
//
// Backend wiring preserved verbatim:
//   asec.threatIntelShareGet(siteId)              -> { share: bool }
//   asec.threatIntelShareSet(siteId, enabled)     -> { enabled }
//   asec.managedRulesList(siteId)                 -> { items: [...] }
//   asec.managedRulePatch(siteId, id, { state })
//
// Hero counters (recon blocked / shadow matches / you contributed) and
// the per-site Activity tab use sample data until live endpoints are wired.

import React from "react";
import {
  Stack, Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Select, MenuItem, Chip, Grid,
} from "@mui/material";
import {
  Share2, ListChecks, Shield, ShieldCheck, ShieldX, Eye, UploadCloud, Network, Activity,
  Search, ChevronRight, ChevronLeft, Check, X, Info, MapPin, Bug, ScanLine,
  KeyRound, GitMerge, Settings2, BarChart3, Radio, Hash, Combine, Zap, Bell, Award,
} from "lucide-react";

import asec from "../../../../../utils/axios/v1/asecAxios";
import colors from "../../../../../utils/colors";
import { useNotification } from "../../../../../contexts/NotificationContext";
import LayerdIcon from "../../../../common/LayerdIcon";
import { GhostButton } from "../../../../common/Button";
import { GreenSwitch } from "../../../../common/Greens";
import StickySaveBar from "../../../../common/StickySaveBar";
import { collectObjectDiff, enabledLabel, fieldChange } from "../../../../common/stickySaveBarUtils";
import AsecPage from "./AsecPage";
import useAsecPermissions from "./useAsecPermissions";
import {
  asecBtnOutlinedSm,
  asecSettingsTableHeadSx,
  asecSettingsTableBodySx,
  filterSelectSx,
  ASEC_ENABLED_TAG,
  asecEnabledTagChipSx,
  ASEC_LEARN_TAG,
  asecLearnTagChipSx,
} from "./asecUi";
import {
  AsecSectionCard,
  AsecMetricStrip,
  AsecStatusPill,
  AsecPageTabs,
  AsecMono,
  AsecSettingsTableCard,
  AsecHeroStatValue,
} from "./components/asecPrimitives";
import { AsecEventsFilterBar } from "./components/AsecEventsFilterBar";
import ThreatIntelActivityDrawer from "./components/ThreatIntelActivityDrawer";

const TAB_KEYS = ["rules", "activity", "contribution"];

const CONTRIBUTION_DIFF_ENTRIES = [
  { key: "share", label: "Contribute signals", format: enabledLabel },
  { key: "autoPromote", label: "Auto-promote high-confidence rules", format: enabledLabel },
  { key: "notifyNew", label: "Notify on new enforce rules", format: enabledLabel },
];

function cloneRules(items) {
  return (items || []).map((r) => ({ ...r }));
}

function collectRuleStateDiff(savedRules, rules) {
  const savedById = Object.fromEntries((savedRules || []).map((r) => [r.id, r.state]));
  return (rules || [])
    .filter((r) => savedById[r.id] !== r.state)
    .map((r) => fieldChange(
      r.target_path_template_norm || r.id,
      savedById[r.id] || "—",
      r.state,
    ));
}

const OK_INK = "#0f7a42";
const WARN_INK = "#7a5b10";
const WARN_BG = "#FFF6D0";
const WARN_BORDER = "#FFE89A";
const DANGER_INK = "#A11228";
const DANGER_BG = "#FEECEE";
const DANGER_BORDER = "#FBC9CF";
const SECONDARY_400 = "#C29021";
const INDIGO_BORDER = "rgba(99,113,247,0.24)";

const MANAGED_RULES_COL_COUNT = 8;
const managedRulesColWidth = `${100 / MANAGED_RULES_COL_COUNT}%`;

const ACTIVITY_MATCHES_COL_COUNT = 7;
const activityMatchesColWidth = `${100 / ACTIVITY_MATCHES_COL_COUNT}%`;

const activityBodyTextSx = {
  ...asecSettingsTableBodySx,
  fontVariantNumeric: "tabular-nums",
};

const managedRulesHeadCellSx = {
  ...asecSettingsTableHeadSx,
  py: 1.75,
  lineHeight: "21px",
};

const managedRulesBodyCellSx = {
  ...asecSettingsTableBodySx,
  py: 2,
  lineHeight: "21px",
  verticalAlign: "middle",
};

const tableRowHoverSx = {
  "&:hover": { backgroundColor: colors.neutral[50] },
};

// ── State badge ─────────────────────────────────────────────────────────
function StateBadge({ state, sm }) {
  const conf = state === "enforce"
    ? { icon: Shield, enabled: true }
    : state === "learn"
      ? { icon: Eye, learn: true }
      : { bg: colors.neutral[100], fg: colors.neutral[600], icon: X, bd: colors.neutral[200] };
  return (
    <Chip
      size="small"
      icon={<LayerdIcon icon={conf.icon} size={sm ? 11 : 12} color="inherit" />}
      label={state}
      sx={{
        ...(conf.enabled ? asecEnabledTagChipSx
          : conf.learn ? asecLearnTagChipSx
            : {
              height: sm ? 22 : 26,
              fontSize: sm ? 11 : 12,
              fontWeight: 700,
              letterSpacing: "0.01em",
              backgroundColor: conf.bg,
              color: conf.fg,
              border: `1px solid ${conf.bd}`,
              borderRadius: "999px",
              "& .MuiChip-label": { px: sm ? 1.125 : 1.5 },
              "& .MuiChip-icon": { ml: 0.75, color: "inherit" },
            }),
      }}
    />
  );
}

// ── Verdict badge (Activity tab) ─────────────────────────────────────────
function VerdictBadge({ verdict }) {
  const isBlock = verdict === "block";
  const conf = isBlock
    ? { icon: ShieldX, label: "403 blocked", bg: DANGER_BG, fg: DANGER_INK, bd: DANGER_BORDER }
    : {
      icon: Eye,
      label: "shadow",
      bg: colors.secondary[10],
      fg: SECONDARY_400,
      bd: "rgba(242, 180, 41, 0.35)",
    };
  return (
    <Chip
      size="small"
      icon={<LayerdIcon icon={conf.icon} size={12} color={conf.fg} />}
      label={conf.label}
      sx={{
        height: "auto",
        minHeight: 22,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.01em",
        backgroundColor: conf.bg,
        color: conf.fg,
        border: `1px solid ${conf.bd}`,
        borderRadius: "999px",
        "& .MuiChip-label": { px: 1.125, py: 0.25, lineHeight: 1.3 },
        "& .MuiChip-icon": { ml: 0.75, color: "inherit" },
      }}
    />
  );
}

// ── Category tag ────────────────────────────────────────────────────────
function CategoryTag({ cat }) {
  if (!cat) {
    return <Typography sx={{ fontSize: 12, color: colors.neutral[500] }}>—</Typography>;
  }
  const palette = {
    recon:    { bg: colors.primary[100], fg: colors.primary[500], bd: colors.primary[200], ic: Search,       icColor: colors.primary[300] },
    exploit:  { bg: DANGER_BG, fg: DANGER_INK, bd: DANGER_BORDER, ic: Bug, icColor: DANGER_INK },
    scanner:  { bg: "#F3EEFE", fg: "#6B3FA0", bd: "#E2D6FA", ic: ScanLine, icColor: "#6B3FA0" },
    cred:     { bg: "#FFF1E6", fg: "#B25A1A", bd: "#FBD9BE", ic: KeyRound, icColor: "#B25A1A" },
  }[cat] || { bg: colors.neutral[100], fg: colors.neutral[1000], bd: "rgba(0,0,0,0.08)", ic: Info, icColor: colors.neutral[500] };
  return (
    <Chip
      size="small"
      icon={<LayerdIcon icon={palette.ic} size={12} color={palette.icColor} />}
      label={cat || "—"}
      sx={{
        height: "auto",
                fontSize: 12,
        fontWeight: 600,
        backgroundColor: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.bd}`,
        borderRadius: "6px",
        "& .MuiChip-icon": { ml: 0.75 },
        "& .MuiChip-label": { px: 1.125, py: 0.375 },
      }}
    />
  );
}

// ── Method tag ──────────────────────────────────────────────────────────
function MethodTag({ m }) {
  const palette = m === "GET" ? { bg: "#E8FBEF", fg: OK_INK, bd: "#C0EED4" }
                : m === "POST" ? { bg: colors.primary[100], fg: colors.primary[500], bd: colors.primary[200] }
                : m === "PUT" ? { bg: WARN_BG, fg: WARN_INK, bd: WARN_BORDER }
                : m === "DELETE" ? { bg: DANGER_BG, fg: DANGER_INK, bd: DANGER_BORDER }
                : { bg: colors.neutral[100], fg: colors.neutral[600], bd: colors.neutral[200] };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 54,
        height: 24,
        px: 1.125,
        borderRadius: "6px",
                fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.04em",
        backgroundColor: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.bd}`,
      }}
    >
      {m}
    </Box>
  );
}

// ── Suspicious path heuristic ───────────────────────────────────────────
const SUSPICIOUS_PATH_TOKENS = [".env", ".git", ".aws", "phpmyadmin", "wp-admin", "wp-config", "actuator/env", "swagger", ".docker", ".npmrc"];
function isSuspiciousPath(p) {
  if (!p) return false;
  return SUSPICIOUS_PATH_TOKENS.some((tok) => p.toLowerCase().includes(tok));
}
function categoriseRule(rule) {
  const kind = (rule.pattern_kind || "").toLowerCase();
  if (kind.includes("cred") || kind.includes("auth")) return "cred";
  if (kind.includes("scan")) return "scanner";
  if (kind.includes("exploit") || kind.includes("rce")) return "exploit";
  return "recon";
}

// ════════════════════════════════════════════════════════════════════════
// Sample data (Activity tab)
// ════════════════════════════════════════════════════════════════════════
const SAMPLE_MATCHES = [
  { id: "activity-match-1", time: "14:32:41", since: "8 sec ago",   method: "GET", path: "/.env",            suspicious: true,  ip: "203.0.113.84",  state: "enforce", verdict: "block" },
  { id: "activity-match-2", time: "14:30:12", since: "3 min ago",   method: "GET", path: "/.git/config",     suspicious: true,  ip: "198.51.100.7",  state: "enforce", verdict: "block" },
  { id: "activity-match-3", time: "14:28:55", since: "4 min ago",   method: "GET", path: "/actuator",        suspicious: false, ip: "192.0.2.155",   state: "learn",   verdict: "shadow" },
  { id: "activity-match-4", time: "14:25:03", since: "8 min ago",   method: "GET", path: "/wp-login.php",    suspicious: false, ip: "203.0.113.84",  state: "learn",   verdict: "shadow" },
  { id: "activity-match-5", time: "14:21:40", since: "11 min ago",  method: "GET", path: "/phpmyadmin/",     suspicious: true,  ip: "198.51.100.22", state: "learn",   verdict: "shadow" },
  { id: "activity-match-6", time: "14:18:09", since: "14 min ago",  method: "GET", path: "/cloud-config.yml",suspicious: false, ip: "192.0.2.41",    state: "learn",   verdict: "shadow" },
  { id: "activity-match-7", time: "14:12:33", since: "20 min ago",  method: "GET", path: "/v2/swagger.json", suspicious: true,  ip: "203.0.113.84",  state: "learn",   verdict: "shadow" },
];

// ════════════════════════════════════════════════════════════════════════
// Hero → AsecMetricStrip
// ════════════════════════════════════════════════════════════════════════
function buildHeroMetrics(share, rules) {
  const enforce = rules.filter((r) => r.state === "enforce").length;
  const learn = rules.filter((r) => r.state === "learn").length;
  return [
    {
      key: "contribution",
      label: "Contribution",
      icon: Share2,
      highlight: true,
      value: (
        <AsecHeroStatValue
          primary={share ? "On" : "Off"}
          pill={(
            <AsecStatusPill kind={share ? "indigo" : "default"}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "currentColor",
                    animation: share ? "ldPulse 1.6s ease-in-out infinite" : "none",
                    "@keyframes ldPulse": {
                      "0%, 100%": { opacity: 0.5 },
                      "50%": { opacity: 1 },
                    },
                  }}
                />
                {share ? "Sharing signals" : "Receiving only"}
              </Stack>
            </AsecStatusPill>
          )}
        />
      ),
      sub: (
        <>
          Anonymised sha256 → <AsecMono>Layerd Threat Intel</AsecMono>
        </>
      ),
    },
    {
      key: "fleet-rules",
      label: "Fleet rules",
      icon: ListChecks,
      value: rules.length.toLocaleString(),
      sub: (
        <>
          {learn} learn · <Box component="strong" sx={{ color: OK_INK }}>{enforce} enforce</Box>
        </>
      ),
    },
    {
      key: "recon-blocked",
      label: "Recon blocked · 24h",
      icon: ShieldX,
      value: "—",
      valueSx: { color: DANGER_INK },
      sub: "Not available yet",
    },
    {
      key: "shadow-matches",
      label: "Shadow matches · 24h",
      icon: Eye,
      value: "—",
      valueSx: { color: SECONDARY_400 },
      sub: "Not available yet",
    },
    {
      key: "you-contributed",
      label: "You contributed",
      icon: UploadCloud,
      value: "—",
      sub: "Not available yet",
    },
  ];
}

// ════════════════════════════════════════════════════════════════════════
// Contribute banner
// ════════════════════════════════════════════════════════════════════════
function ContributeBanner({ on, onChange, disabled }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        p: 2.5,
        borderRadius: "12px",
        border: `1px solid ${on ? INDIGO_BORDER : colors.neutral[200]}`,
        background: on
          ? "linear-gradient(180deg,rgba(99,113,247,0.06),rgba(99,113,247,0.02))"
          : "#FAFBFC",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.75} sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            backgroundColor: "#fff",
            border: `1px solid ${on ? INDIGO_BORDER : colors.neutral[200]}`,
            color: on ? colors.primary[300] : colors.neutral[500],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <LayerdIcon icon={Network} size="lg" color={on ? "primaryLight" : "neutral"} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.125} flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em", color: colors.neutral[900] }}>
              Contribute to the fleet immune system
            </Typography>
            <AsecStatusPill kind={on ? "indigo" : "default"}>
              <Stack direction="row" alignItems="center" spacing={0.625}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor" }} />
                {on ? "On" : "Off"}
              </Stack>
            </AsecStatusPill>
          </Stack>
          <Typography sx={{ fontSize: 12, color: colors.neutral[600], mt: 0.375, maxWidth: 820, lineHeight: "18px" }}>
            When on, this site sends anonymised (<AsecMono>sha256</AsecMono>-hashed) recon and attack signals
            to <AsecMono>Layerd Threat Intel</AsecMono>. Other Layerd customers benefit from what we learn
            here; you benefit from what they learn. When off, this site still <i>receives</i> managed
            rules but doesn&apos;t contribute.
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
        <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>{on ? "On" : "Off"}</Typography>
        <GreenSwitch
          checked={on}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </Stack>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Managed rules tab
// ════════════════════════════════════════════════════════════════════════
function EnforceToggle({ enforced, onChange, disabled }) {
  return (
    <GreenSwitch
      checked={enforced}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
    />
  );
}

function FleetPips({ count }) {
  const capped = Math.min(count || 0, 5);
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box component="svg" width={48} height={14} viewBox="0 0 60 14">
        <polyline
          fill="none"
          stroke="#A8ADBA"
          strokeWidth="1.5"
          points={`0,${14 - Math.min(11, count || 1)} 12,${14 - Math.min(11, (count || 1) - 1)} 24,${14 - Math.min(11, (count || 1) - 2)} 36,${14 - Math.min(11, count || 1)} 48,${14 - Math.min(11, (count || 1) - 1)} 60,${14 - Math.min(11, (count || 1) - 2)}`}
        />
      </Box>
      <Stack direction="row" spacing={0.25}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Box
            key={i}
            sx={{
              width: 6,
              height: 14,
              borderRadius: "2px",
              backgroundColor: i < capped ? colors.primary[300] : colors.neutral[300],
            }}
          />
        ))}
      </Stack>
      <Typography
        sx={{
          fontWeight: 600,
          color: colors.neutral[900],
          minWidth: 14,
          fontVariantNumeric: "tabular-nums",
          fontSize: 14,
        }}
      >
        {count != null ? count : "—"}
      </Typography>
    </Stack>
  );
}

function RulesTab({ rules, onSetState, canConfigure, saving, seenHereSet, search, setSearch }) {
  const [stateFilter, setStateFilter] = React.useState("all");
  const [catFilter, setCatFilter] = React.useState("all");

  const rulesByCat = React.useMemo(() => {
    const out = {};
    rules.forEach((r) => { const c = categoriseRule(r); out[c] = (out[c] || 0) + 1; });
    return out;
  }, [rules]);

  const filtered = rules.filter((r) => {
    if (search) {
      const q = search.toLowerCase();
      if (!((r.target_path_template_norm || "") + (r.pattern_kind || "")).toLowerCase().includes(q)) return false;
    }
    if (stateFilter === "all") {/* pass */}
    else if (stateFilter === "seen_here") { if (!seenHereSet.has(r.id)) return false; }
    else if (r.state !== stateFilter) return false;
    if (catFilter !== "all" && categoriseRule(r) !== catFilter) return false;
    return true;
  });

  return (
    <Stack spacing={1.75}>
      <AsecEventsFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search target path…"
        categoryFilter={stateFilter}
        onCategoryChange={setStateFilter}
        chips={[
          { key: "all",       label: "All",       ct: rules.length },
          { key: "enforce",   label: "Enforce",   ct: rules.filter((r) => r.state === "enforce").length },
          { key: "learn",     label: "Learn",     ct: rules.filter((r) => r.state === "learn").length },
          { key: "seen_here", label: "Seen here", ct: seenHereSet.size },
        ]}
      >
        <Select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          sx={{ ...filterSelectSx, minWidth: 150 }}
        >
          <MenuItem value="all">All categories ({rules.length})</MenuItem>
          <MenuItem value="recon">Recon ({rulesByCat.recon || 0})</MenuItem>
          <MenuItem value="exploit">Exploit ({rulesByCat.exploit || 0})</MenuItem>
          <MenuItem value="scanner">Scanner ({rulesByCat.scanner || 0})</MenuItem>
        </Select>
      </AsecEventsFilterBar>

      <AsecSettingsTableCard>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.25,
            py: 1.75,
            borderBottom: `1px solid ${colors.neutral[200]}`,
            gap: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <LayerdIcon icon={ListChecks} size="sm" color="neutral" />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000] }}>
              Managed rules{" "}
              <Box component="span" sx={{ color: colors.neutral[600], fontWeight: 400 }}>· fleet-wide</Box>
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
            generated by the cross-customer mining pipeline · promote a learn rule to enforce inline
          </Typography>
        </Stack>

        <Table size="small" sx={{ fontVariantNumeric: "tabular-nums", tableLayout: "fixed", width: "100%" }}>
          <colgroup>
            {Array.from({ length: MANAGED_RULES_COL_COUNT }).map((_, i) => (
              <col
                key={i}
                style={{ width: i === MANAGED_RULES_COL_COUNT - 1 ? "88px" : managedRulesColWidth }}
              />
            ))}
          </colgroup>
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
              <TableCell sx={managedRulesHeadCellSx}>State</TableCell>
              <TableCell sx={managedRulesHeadCellSx}>Category</TableCell>
              <TableCell sx={managedRulesHeadCellSx}>Method</TableCell>
              <TableCell sx={managedRulesHeadCellSx}>Target path</TableCell>
              <TableCell sx={managedRulesHeadCellSx}>Customers</TableCell>
              <TableCell sx={managedRulesHeadCellSx}>First seen</TableCell>
              <TableCell sx={managedRulesHeadCellSx}>Last seen</TableCell>
              <TableCell sx={{ ...managedRulesHeadCellSx, textAlign: "center" }} align="center">Enforce</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography textAlign="center" sx={{ ...asecSettingsTableBodySx, py: 5, color: colors.neutral[600] }}>
                    {rules.length === 0
                      ? "No managed rules yet. As more sites opt in to sharing, the fleet picks up patterns and surfaces them here."
                      : "No rules match this filter."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r) => {
              const cat = categoriseRule(r);
              const susp = isSuspiciousPath(r.target_path_template_norm);
              const seenHere = seenHereSet.has(r.id);
              return (
                <TableRow key={r.id} sx={tableRowHoverSx}>
                  <TableCell sx={managedRulesBodyCellSx}><StateBadge state={r.state} /></TableCell>
                  <TableCell sx={managedRulesBodyCellSx}><CategoryTag cat={cat} /></TableCell>
                  <TableCell sx={managedRulesBodyCellSx}>
                    <MethodTag m={(r.target_method || "ANY").toUpperCase()} />
                  </TableCell>
                  <TableCell sx={{ ...managedRulesBodyCellSx, overflow: "hidden" }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                      <Typography
                        component="span"
                        title={r.target_path_template_norm || undefined}
                        sx={{
                          fontSize: 12,
                          lineHeight: "18px",
                          color: susp ? DANGER_INK : colors.neutral[900],
                          fontWeight: susp ? 600 : 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {r.target_path_template_norm || "—"}
                      </Typography>
                      {seenHere && (
                        <Chip
                          size="small"
                          icon={<LayerdIcon icon={MapPin} size={11} color={DANGER_INK} />}
                          label="seen here"
                          sx={{
                            height: "auto",
                            fontSize: 12,
                            fontWeight: 600,
                            color: DANGER_INK,
                            backgroundColor: DANGER_BG,
                            border: `1px solid ${DANGER_BORDER}`,
                            borderRadius: "5px",
                            "& .MuiChip-label": { px: 0.875, py: 0.25 },
                          }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={managedRulesBodyCellSx}>
                    <FleetPips count={r.customer_count} />
                  </TableCell>
                  <TableCell sx={managedRulesBodyCellSx}>
                    <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700], fontVariantNumeric: "tabular-nums" }}>
                      {r.first_seen ? new Date(r.first_seen).toLocaleDateString() : "—"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[500], fontVariantNumeric: "tabular-nums" }}>
                      {r.first_seen ? new Date(r.first_seen).toLocaleTimeString() : ""}
                    </Typography>
                  </TableCell>
                  <TableCell sx={managedRulesBodyCellSx}>
                    <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700], fontVariantNumeric: "tabular-nums" }}>
                      {r.last_seen ? new Date(r.last_seen).toLocaleDateString() : "—"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[500], fontVariantNumeric: "tabular-nums" }}>
                      {r.last_seen ? new Date(r.last_seen).toLocaleTimeString() : ""}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...managedRulesBodyCellSx, textAlign: "center" }} align="center">
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <EnforceToggle
                        enforced={r.state === "enforce"}
                        disabled={!canConfigure || saving}
                        onChange={(on) => onSetState(r.id, on ? "enforce" : "learn")}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.25,
            py: 1.5,
            borderTop: `1px solid ${colors.neutral[200]}`,
          }}
        >
          <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
            Showing <Box component="strong" sx={{ color: colors.neutral[900] }}>{filtered.length}</Box> of{" "}
            <Box component="strong" sx={{ color: colors.neutral[900] }}>{rules.length}</Box> rules{" "}
            · <Box component="strong" sx={{ color: OK_INK }}>{rules.filter((r) => r.state === "enforce").length}</Box> enforcing
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <GhostButton
              size="small"
              sx={asecBtnOutlinedSm}
              startIcon={<LayerdIcon icon={ChevronLeft} size="sm" color="neutral" />}
            >
              Previous
            </GhostButton>
            <GhostButton
              size="small"
              sx={asecBtnOutlinedSm}
              endIcon={<LayerdIcon icon={ChevronRight} size="sm" color="neutral" />}
            >
              Next
            </GhostButton>
          </Stack>
        </Stack>
      </AsecSettingsTableCard>
    </Stack>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Activity tab
// ════════════════════════════════════════════════════════════════════════
function ActivityTab() {
  const total = SAMPLE_MATCHES.length;
  const [activityDrawer, setActivityDrawer] = React.useState(null);

  return (
    <AsecSettingsTableCard>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2.25,
          py: 1.75,
          borderBottom: `1px solid ${colors.neutral[200]}`,
          gap: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <LayerdIcon icon={Activity} size="sm" color="neutral" />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000] }}>
            Managed-rule matches on this site
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
          last 24h · sample data
        </Typography>
      </Stack>

      <Table size="small" sx={{ fontVariantNumeric: "tabular-nums", tableLayout: "fixed", width: "100%" }}>
        <colgroup>
          {Array.from({ length: ACTIVITY_MATCHES_COL_COUNT }).map((_, i) => (
            <col key={i} style={{ width: activityMatchesColWidth }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
            <TableCell sx={managedRulesHeadCellSx}>Time</TableCell>
            <TableCell sx={managedRulesHeadCellSx}>Method</TableCell>
            <TableCell sx={managedRulesHeadCellSx}>Path hit</TableCell>
            <TableCell sx={managedRulesHeadCellSx}>Source IP</TableCell>
            <TableCell sx={managedRulesHeadCellSx}>Matched state</TableCell>
            <TableCell sx={managedRulesHeadCellSx} align="right">Verdict</TableCell>
            <TableCell sx={managedRulesHeadCellSx} align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {SAMPLE_MATCHES.map((r) => (
            <TableRow
              key={r.id}
              hover
              onClick={() => setActivityDrawer(r)}
              sx={{ ...tableRowHoverSx, cursor: "pointer" }}
            >
              <TableCell sx={managedRulesBodyCellSx}>
                <Stack spacing={0.5}>
                  <Typography sx={{
                    ...activityBodyTextSx,
                    color: colors.neutral[1000],
                  }}>
                    {r.time}
                  </Typography>
                  <Typography sx={{
                    ...activityBodyTextSx,
                    color: colors.neutral[500],
                  }}>
                    {r.since}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell sx={managedRulesBodyCellSx}>
                <MethodTag m={r.method} />
              </TableCell>
              <TableCell sx={{ ...managedRulesBodyCellSx, overflow: "hidden" }}>
                <Typography
                  title={r.path}
                  sx={{
                    ...activityBodyTextSx,
                    color: r.suspicious ? DANGER_INK : colors.neutral[900],
                    fontWeight: r.suspicious ? 600 : 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.path}
                </Typography>
              </TableCell>
              <TableCell sx={managedRulesBodyCellSx}>
                <Typography sx={activityBodyTextSx}>
                  {r.ip}
                </Typography>
              </TableCell>
              <TableCell sx={managedRulesBodyCellSx}>
                <StateBadge state={r.state} />
              </TableCell>
              <TableCell sx={managedRulesBodyCellSx} align="right">
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <VerdictBadge verdict={r.verdict} />
                </Box>
              </TableCell>
              <TableCell sx={managedRulesBodyCellSx} align="right">
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setActivityDrawer(r)}
                    aria-label="View match details"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0.5,
                      border: 0,
                      background: "none",
                      cursor: "pointer",
                      borderRadius: "6px",
                      "&:hover": { backgroundColor: colors.neutral[100] },
                    }}
                  >
                    <LayerdIcon icon={Eye} size={18} color="neutral" />
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2.25,
          py: 1.5,
          borderTop: `1px solid ${colors.neutral[200]}`,
        }}
      >
        <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
          Showing{" "}
          <Box component="strong" sx={{ color: colors.neutral[900] }}>1–{total}</Box> of{" "}
          <Box component="strong" sx={{ color: colors.neutral[900] }}>{total}</Box> matches
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <GhostButton
            size="small"
            sx={asecBtnOutlinedSm}
            startIcon={<LayerdIcon icon={ChevronLeft} size="sm" color="neutral" />}
          >
            Previous
          </GhostButton>
          <GhostButton
            size="small"
            sx={asecBtnOutlinedSm}
            endIcon={<LayerdIcon icon={ChevronRight} size="sm" color="neutral" />}
          >
            Next
          </GhostButton>
        </Stack>
      </Stack>

      <ThreatIntelActivityDrawer
        open={!!activityDrawer}
        row={activityDrawer}
        onClose={() => setActivityDrawer(null)}
      />
    </AsecSettingsTableCard>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Contribution tab
// ════════════════════════════════════════════════════════════════════════
function PipelineNode({ icon, name, ds, last }) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 1,
        position: "relative",
        px: 1,
      }}
    >
      {!last && (
        <Box
          sx={{
            position: "absolute",
            top: 21,
            left: "60%",
            right: "-40%",
            height: 2,
            background: `repeating-linear-gradient(90deg, ${colors.neutral[400]} 0 5px, transparent 5px 10px)`,
          }}
        />
      )}
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          backgroundColor: "rgba(99,113,247,0.08)",
          border: "1px solid rgba(99,113,247,0.20)",
          color: colors.primary[300],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        <LayerdIcon icon={icon} size="lg" color="primaryLight" />
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.neutral[900] }}>{name}</Typography>
      <Typography sx={{ fontSize: 12, color: colors.neutral[600], lineHeight: "15px" }}>{ds}</Typography>
    </Box>
  );
}

function ToggleRow({ icon, name, desc, on, onChange, disabled, last }) {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1.5}
      sx={{
        py: 1.75,
        borderBottom: last ? 0 : `1px solid rgba(0,0,0,0.08)`,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.375 }}>
          <LayerdIcon icon={icon} size="sm" color="neutral" />
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: "25px", color: colors.neutral[900] }}>{name}</Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: colors.neutral[600], lineHeight: "25px", maxWidth: 560 }}>
          {desc}
        </Typography>
      </Box>
      <GreenSwitch
        checked={on}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ mt: 0.25 }}
      />
    </Stack>
  );
}

function ShareItem({ yes, children }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        py: 1.25,
        borderBottom: `1px solid rgba(0,0,0,0.08)`,
        fontSize: 12,
      }}
    >
      <LayerdIcon icon={yes ? Check : X} size={15} color={yes ? OK_INK : DANGER_INK} />
      <Typography sx={{ color: yes ? colors.neutral[900] : colors.neutral[600], lineHeight: "25px" }}>{children}</Typography>
    </Stack>
  );
}

function ContributionTab({ share, setShare, autoPromote, setAutoPromote, notifyNew, setNotifyNew, canConfigure, saving }) {
  return (
    <Grid container spacing={2.5} alignItems="flex-start">
      <Grid item xs={12} md={7}>
        <Stack spacing={2.5}>
          <AsecSectionCard title="How the pipeline works" icon={GitMerge} radius="12px">
            <Stack direction="row" alignItems="stretch">
              <PipelineNode icon={Radio} name="Observe" ds="Recon & attack signals seen at the edge" />
              <PipelineNode icon={Hash} name="Hash" ds="Paths & IPs sha256-hashed, PII stripped" />
              <PipelineNode icon={Combine} name="Mine" ds="Cross-customer correlation finds patterns" />
              <PipelineNode icon={ShieldCheck} name="Distribute" ds="Managed rules pushed to every site ~1s" last />
            </Stack>
          </AsecSectionCard>

          <AsecSectionCard title="Contribution settings" icon={Settings2} radius="12px" noPadding bodySx={{ px: 2.25, pb: 1.5 }}>
            <ToggleRow
              icon={Share2}
              name="Contribute signals"
              desc="Send anonymised recon & attack telemetry to the fleet. You always receive managed rules either way."
              on={share}
              disabled={!canConfigure || saving}
              onChange={setShare}
            />
            <ToggleRow
              icon={Zap}
              name="Auto-promote high-confidence rules"
              desc={(
                <>
                  When the fleet flags a rule with &gt;<Box component="strong" sx={{ color: colors.neutral[900] }}>20</Box> customers and zero false positives, move it from <AsecMono>learn</AsecMono> to <AsecMono>enforce</AsecMono> automatically. (Site-local preference — does not auto-promote yet; tracking pending.)
                </>
              )}
              on={autoPromote}
              disabled={!canConfigure || saving}
              onChange={setAutoPromote}
            />
            <ToggleRow
              icon={Bell}
              name="Notify on new enforce rules"
              desc="Slack + email when a rule transitions to enforce on this site. (Hooks into the alert rules system once configured.)"
              on={notifyNew}
              disabled={!canConfigure || saving}
              onChange={setNotifyNew}
              last
            />
          </AsecSectionCard>
        </Stack>
      </Grid>

      <Grid item xs={12} md={5}>
        <Stack spacing={2.5}>
          <AsecSectionCard
            title="What gets shared"
            icon={Eye}
            radius="12px"
            right={<AsecStatusPill kind="ok"><Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.625 }}><Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor" }} />PII-safe</Box></AsecStatusPill>}
          >
            <ShareItem yes>Hashed request path — <AsecMono>sha256(/.env)</AsecMono></ShareItem>
            <ShareItem yes>Method &amp; response code</ShareItem>
            <ShareItem yes>Coarse geo &amp; ASN of the source</ShareItem>
            <ShareItem yes>Attack category &amp; timestamp</ShareItem>
            <ShareItem yes={false}>Raw IPs, headers, bodies, or query strings</ShareItem>
            <ShareItem yes={false}>Anything identifying your site or users</ShareItem>
          </AsecSectionCard>

          <AsecSectionCard title="Your contribution · 7d" icon={BarChart3} radius="12px">
            <Stack spacing={1.75}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                <Box>
                  <Typography sx={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums", color: colors.neutral[900] }}>-</Typography>
                  <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>signals contributed</Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums", color: colors.primary[300] }}>-</Typography>
                  <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>rules you seeded</Typography>
                </Box>
              </Stack>
              <Box component="svg" viewBox="0 0 320 56" width="100%" height={56} preserveAspectRatio="none">
                <polyline
                  fill="none"
                  stroke={colors.primary[300]}
                  strokeWidth="2"
                  points="0,44 45,40 90,42 135,30 180,34 225,20 270,24 320,12"
                />
                <polygon
                  fill="rgba(99,113,247,0.08)"
                  points="0,44 45,40 90,42 135,30 180,34 225,20 270,24 320,12 320,56 0,56"
                />
              </Box>
              <Stack direction="row" alignItems="center" spacing={0.875}>
                <LayerdIcon icon={Award} size="sm" color="primaryLight" />
                <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
                  Sparkline above is illustrative.
                </Typography>
              </Stack>
            </Stack>
          </AsecSectionCard>
        </Stack>
      </Grid>
    </Grid>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Body
// ════════════════════════════════════════════════════════════════════════
function Body({ siteId, registerReload }) {
  const { showSuccess, showError } = useNotification();
  const { canConfigure } = useAsecPermissions();
  const [tab, setTab] = React.useState("rules");

  const [share, setShare] = React.useState(false);
  const [savedContribution, setSavedContribution] = React.useState({
    share: false,
    autoPromote: false,
    notifyNew: true,
  });
  const [rules, setRules] = React.useState([]);
  const [savedRules, setSavedRules] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [autoPromote, setAutoPromote] = React.useState(false);
  const [notifyNew, setNotifyNew] = React.useState(true);

  const contributionDraft = React.useMemo(
    () => ({ share, autoPromote, notifyNew }),
    [share, autoPromote, notifyNew],
  );

  const seenHereSet = React.useMemo(() => {
    const s = new Set();
    for (const r of rules) {
      if (r.seen_here || r.seen_on_site || isSuspiciousPath(r.target_path_template_norm)) {
        s.add(r.id);
      }
    }
    return s;
  }, [rules]);

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const [s, r] = await Promise.all([
        asec.threatIntelShareGet(siteId).catch(() => ({ data: { share: false } })),
        asec.managedRulesList(siteId).catch(() => ({ data: { items: [] } })),
      ]);
      const nextShare = !!s.data?.share;
      setShare(nextShare);
      setSavedContribution((prev) => ({
        share: nextShare,
        autoPromote: prev.autoPromote,
        notifyNew: prev.notifyNew,
      }));
      const items = r.data?.items || [];
      setRules(items);
      setSavedRules(cloneRules(items));
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    }
  }, [siteId, showError]);

  React.useEffect(() => { reload(); }, [reload]);
  React.useEffect(() => {
    if (registerReload) registerReload(reload);
  }, [registerReload, reload]);

  const contributionModifiedFields = React.useMemo(
    () => collectObjectDiff(savedContribution, contributionDraft, CONTRIBUTION_DIFF_ENTRIES),
    [savedContribution, contributionDraft],
  );

  const ruleModifiedFields = React.useMemo(
    () => collectRuleStateDiff(savedRules, rules),
    [savedRules, rules],
  );

  const stickyModifiedFields = React.useMemo(
    () => [...contributionModifiedFields, ...ruleModifiedFields],
    [contributionModifiedFields, ruleModifiedFields],
  );
  const stickyModifiedCount = stickyModifiedFields.length;

  const onDiscardChanges = React.useCallback(() => {
    setShare(savedContribution.share);
    setAutoPromote(savedContribution.autoPromote);
    setNotifyNew(savedContribution.notifyNew);
    setRules(cloneRules(savedRules));
  }, [savedContribution, savedRules]);

  const onApplyChanges = React.useCallback(async () => {
    if (!canConfigure || !siteId || stickyModifiedCount === 0) return;
    setSaving(true);
    try {
      if (savedContribution.share !== share) {
        await asec.threatIntelShareSet(siteId, share);
      }
      const savedById = Object.fromEntries(savedRules.map((r) => [r.id, r.state]));
      const changedRules = rules.filter((r) => savedById[r.id] !== r.state);
      if (changedRules.length > 0) {
        await Promise.all(
          changedRules.map((r) => asec.managedRulePatch(siteId, r.id, { state: r.state })),
        );
      }
      const nextSavedContribution = { share, autoPromote, notifyNew };
      const nextSavedRules = cloneRules(rules);
      setSavedContribution(nextSavedContribution);
      setSavedRules(nextSavedRules);
      if (changedRules.length > 0 && savedContribution.share !== share) {
        showSuccess("Threat Intel settings and managed rules saved.");
      } else if (changedRules.length > 0) {
        showSuccess(`Saved ${changedRules.length} managed rule${changedRules.length === 1 ? "" : "s"}.`);
      } else if (savedContribution.share !== share) {
        showSuccess(share
          ? "This site now contributes anonymised signals to the fleet immune system."
          : "This site no longer contributes signals (still receives managed rules).");
      } else {
        showSuccess("Contribution settings saved.");
      }
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }, [
    autoPromote,
    canConfigure,
    notifyNew,
    rules,
    savedContribution,
    savedRules,
    share,
    showError,
    showSuccess,
    siteId,
    stickyModifiedCount,
  ]);

  const updateRuleState = React.useCallback((id, next) => {
    if (!canConfigure || saving) return;
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, state: next } : r)));
  }, [canConfigure, saving]);

  const tabIdx = TAB_KEYS.indexOf(tab);

  return (
    <Stack spacing={2.5}>
      <AsecMetricStrip metrics={buildHeroMetrics(share, rules)} />
      <ContributeBanner
        on={share}
        onChange={setShare}
        disabled={!canConfigure || saving}
      />
      <AsecPageTabs
        value={tabIdx}
        onChange={(_, idx) => setTab(TAB_KEYS[idx])}
        tabs={[
          { key: "rules", label: "Managed rules", count: rules.length },
          { key: "activity", label: "Activity on this site", count: SAMPLE_MATCHES.length },
          { key: "contribution", label: "Contribution" },
        ]}
        right={(
          <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 0.5 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: colors.green[200] }} />
            <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>Pipeline syncs ~1s</Typography>
          </Stack>
        )}
      />

      {tab === "rules" && (
        <RulesTab
          rules={rules}
          onSetState={updateRuleState}
          canConfigure={canConfigure}
          saving={saving}
          seenHereSet={seenHereSet}
          search={search}
          setSearch={setSearch}
        />
      )}
      {tab === "activity" && <ActivityTab />}
      {tab === "contribution" && (
        <ContributionTab
          share={share}
          setShare={setShare}
          autoPromote={autoPromote}
          setAutoPromote={setAutoPromote}
          notifyNew={notifyNew}
          setNotifyNew={setNotifyNew}
          canConfigure={canConfigure}
          saving={saving}
        />
      )}

      <StickySaveBar
        open={canConfigure && stickyModifiedCount > 0}
        modifiedCount={stickyModifiedCount}
        modifiedFields={stickyModifiedFields}
        onDiscard={onDiscardChanges}
        onApply={onApplyChanges}
        isApplying={saving}
      />
    </Stack>
  );
}

export default function ThreatIntel() {
  const reloadRef = React.useRef(null);

  return (
    <AsecPage
      featureKey="api.auth_primitives"
      helpTooltip="Cross-customer immune system: when one customer's site is attacked, the pattern propagates to other opted-in sites as managed rules. learn state = shadow; enforce state = active 403."
      title="Threat Intel"
      helmetTitle="API Security · Threat Intel — Layerd"
      pageDescription={(
        <>
          Cross-customer threat intelligence. Rules are mined from anonymised recon and attack signals
          across the Layerd fleet. <Box component="strong" sx={{ color: colors.neutral[900] }}>learn</Box> rules run in shadow mode
          (logged, not blocked); <Box component="strong" sx={{ color: colors.neutral[900] }}>enforce</Box> rules return <AsecMono>403</AsecMono>{" "}
          on the matched method + path.
        </>
      )}
      onRefresh={async () => { await reloadRef.current?.(); }}
    >
      {(siteId) => (
        <Body
          siteId={siteId}
          registerReload={(fn) => { reloadRef.current = fn; }}
        />
      )}
    </AsecPage>
  );
}
